import { Label } from '@/components/ui/label'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { CalenderOutlineIcon } from '@/components/Icons'
import { EventTimezones } from '@/constants/event'
import { useFormContext, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import { DatePicker } from '../ui/date-picker'
import { TimePicker } from '../ui/time-picker'
import { useParams } from 'react-router-dom'
import { normalizeDate } from '@/utils/dateUtils'

export default function EventDateTimeForm() {
  const { id } = useParams<{ id: string }>()
  const { control, watch } = useFormContext<EventFormDataType>()
  const endDate = watch('endDate')

  const handleStartDateDisable = (date: Date) => {
    const today = normalizeDate(new Date())
    const checkDate = normalizeDate(date)
    return checkDate < today
  }

  const handleEndDateDisable = (date: Date) => {
    const today = normalizeDate(new Date())
    const startDate = watch('startDate')
    const earliestAllowed = startDate ? normalizeDate(startDate) : today

    const checkDate = normalizeDate(date)
    return checkDate < earliestAllowed
  }

  const handleRegistrationDeadlineDisable = (date: Date) => {
    const today = normalizeDate(new Date())

    if (date < today) return true

    if (endDate) {
      const checkEndDate = normalizeDate(endDate)
      const checkDate = normalizeDate(date)
      if (checkDate >= checkEndDate) {
        return true
      }
    }

    return false
  }

  return (
    <>
      {/* Date & Time */}
      <CardHeader className="border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <CalenderOutlineIcon className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950">
          Date & Time
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Start Date & Time / End Date & Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Date & Time */}
          <div className="space-y-3">
            {/* Start Date */}
            <div className="space-y-1">
              <Label
                variant="required"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Start Date
              </Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select start date"
                      className="w-full"
                      disableDate={handleStartDateDisable}
                      clearable={!id}
                    />
                    {error && (
                      <p
                        className="text-sm text-ss-critical-200 mt-1"
                        role="alert"
                      >
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            {/* Start Time */}
            <div className="space-y-1">
              <Label
                variant="required"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Start Time
              </Label>
              <Controller
                name="startTime"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TimePicker value={field.value} onChange={field.onChange} />
                    {error && (
                      <p
                        className="text-sm text-ss-critical-200 mt-1"
                        role="alert"
                      >
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {/* End Date & Time */}
          <div className="space-y-3">
            {/* End Date */}
            <div className="space-y-1">
              <Label
                variant="required"
                className="text-sm font-medium text-ss-neutral-950"
              >
                End Date
              </Label>
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select end date"
                      className="w-full"
                      disableDate={handleEndDateDisable}
                      clearable={!id}
                    />
                    {error && (
                      <p
                        className="text-sm text-ss-critical-200 mt-1"
                        role="alert"
                      >
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            {/* End Time */}
            <div className="space-y-1">
              <Label
                variant="required"
                className="text-sm font-medium text-ss-neutral-950"
              >
                End Time
              </Label>
              <Controller
                name="endTime"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TimePicker value={field.value} onChange={field.onChange} />
                    {error && (
                      <p
                        className="text-sm text-ss-critical-200 mt-1"
                        role="alert"
                      >
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </div>

        {/* Time Zone / Registration Deadline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label
              variant="required"
              className="text-sm font-medium text-ss-neutral-950 w-full"
            >
              Time Zone
            </Label>
            <Controller
              name="timeZone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      size="lg"
                      className={cn(
                        'h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full',
                        error && 'border-ss-critical-200'
                      )}
                    >
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent className="max-h-96">
                      {Object.values(EventTimezones).map(tz => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="space-y-1">
            <Label
              variant="required"
              className="text-sm font-medium text-ss-neutral-950"
            >
              Registration Deadline
            </Label>
            <Controller
              name="registrationDeadline"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select end date"
                    id="registration-end-date-button"
                    aria-label="Select end date"
                    className="w-full"
                    disableDate={handleRegistrationDeadlineDisable}
                    toYear={new Date().getFullYear() + 5}
                    clearable={!id}
                  />

                  {error && (
                    <p
                      className="text-sm text-ss-critical-200 mt-1"
                      role="alert"
                    >
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </CardContent>
    </>
  )
}
